import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';

const FooterOne = () => {
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/api/settings');
                if (res.data) setSettings(res.data);
            } catch (err) {
                console.error("Failed to load footer settings", err);
            }
        };
        fetchSettings();
    }, []);

    const phone = settings?.storePhone || settings?.phone || '+91 9449 818 329';
    const email = settings?.storeEmail || settings?.email || 'healthyhomesales@gmail.com';
    const address = settings?.storeAddress || settings?.address || 'A306 Durga Petals Apartment Near rainbow children hospital Doddanekundi Bangalore 560 037';

    const socialLinks = [
        { key: 'facebook', url: settings?.facebookUrl, icon: 'ph-fill ph-facebook-logo', title: 'Facebook' },
        { key: 'instagram', url: settings?.instagramUrl, icon: 'ph-fill ph-instagram-logo', title: 'Instagram' },
        { key: 'twitter', url: settings?.twitterUrl, icon: 'ph-fill ph-twitter-logo', title: 'Twitter / X' },
        { key: 'youtube', url: settings?.youtubeUrl, icon: 'ph-fill ph-youtube-logo', title: 'YouTube' },
    ].filter(item => item.url && typeof item.url === 'string' && item.url.trim() !== '');

    return (
        <footer className="footer pt-60 pt-lg-100 pb-32">
            <img
                src="/assets/images/bg/body-bottom-bg.png"
                alt="BG"
                className="body-bottom-bg"
            />

            <style>{`
                .footer-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 32px;
                }

                .footer-item {
                    min-width: 0;
                }

                .footer-contact-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 14px;
                    margin-bottom: 16px;
                }

                .footer-contact-item .contact-icon {
                    width: 32px;
                    height: 32px;
                    flex-shrink: 0;
                    margin-top: 3px;
                }

                .footer-contact-item .contact-text {
                    font-size: 0.9375rem;
                    line-height: 1.55;
                    word-break: break-word;
                    overflow-wrap: break-word;
                }

                /* 576px - 767px (Small tablets & landscape phones): 2 columns */
                @media (min-width: 576px) and (max-width: 767px) {
                    .footer-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        column-gap: 28px;
                        row-gap: 36px;
                    }
                    .footer-col-logo,
                    .footer-col-contact {
                        grid-column: span 2;
                    }
                }

                /* 768px - 1024px (Tablets / iPad): 3-column / 2-row layout */
                @media (min-width: 768px) and (max-width: 1024px) {
                    .footer-grid {
                        grid-template-columns: repeat(6, minmax(0, 1fr));
                        column-gap: 24px;
                        row-gap: 36px;
                        align-items: start;
                    }
                    .footer-col-logo {
                        grid-column: span 3;
                    }
                    .footer-col-quick {
                        grid-column: span 3;
                    }
                    .footer-col-account {
                        grid-column: span 2;
                    }
                    .footer-col-policies {
                        grid-column: span 2;
                    }
                    .footer-col-contact {
                        grid-column: span 2;
                    }
                }

                /* 1025px - 1199px (Small desktops & laptops): 5 columns */
                @media (min-width: 1025px) and (max-width: 1199px) {
                    .footer-grid {
                        grid-template-columns: 1.3fr 0.9fr 0.9fr 0.9fr 1.3fr;
                        column-gap: 20px;
                        row-gap: 0;
                        align-items: start;
                    }
                    .footer-col-logo,
                    .footer-col-quick,
                    .footer-col-account,
                    .footer-col-policies,
                    .footer-col-contact {
                        grid-column: auto;
                    }
                }

                /* 1200px+ (Large desktops): 5 generous columns */
                @media (min-width: 1200px) {
                    .footer-grid {
                        grid-template-columns: 1.4fr 0.85fr 0.85fr 0.9fr 1.4fr;
                        column-gap: 32px;
                        row-gap: 0;
                        align-items: start;
                    }
                    .footer-col-logo,
                    .footer-col-quick,
                    .footer-col-account,
                    .footer-col-policies,
                    .footer-col-contact {
                        grid-column: auto;
                    }
                }
            `}</style>

            <div className="container container-lg">
                <div className="footer-grid">
                    {/* Column 1: Logo and Social Links */}
                    <div className="footer-item footer-col-logo d-flex flex-column wow fadeInUp" data-wow-delay="0.1s">
                        <div className="footer-item__logo mb-auto">
                            <Link to="/">
                                <img
                                    src="/assets/images/logo/logo.png"
                                    alt="Healthy Homes Logo"
                                    style={{
                                        width: '100%',
                                        maxWidth: '240px',
                                        height: 'auto',
                                        maxHeight: '100px',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Link>
                        </div>

                        {socialLinks.length > 0 && (
                            <ul className="flex-align gap-12 mt-20 mt-lg-auto pt-lg-24 flex-wrap">
                                {socialLinks.map((item) => {
                                    const url = item.url.trim();
                                    const formattedUrl = (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//'))
                                        ? url
                                        : (item.key === 'whatsapp' && /^\+?[0-9\s-]+$/.test(url)
                                            ? `https://wa.me/${url.replace(/[^0-9]/g, '')}`
                                            : `https://${url}`);

                                    return (
                                        <li key={item.key}>
                                            <a
                                                href={formattedUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-40 h-40 flex-center bg-main-100 text-main-600 text-lg rounded-circle hover-bg-main-600 hover-text-white transition-2"
                                                aria-label={item.title}
                                                title={item.title}
                                            >
                                                <i className={item.icon} />
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {/* Column 2: Quick Links */}
                    <div className="footer-item footer-col-quick pe-xl-2 wow fadeInUp" data-wow-delay="0.2s">
                        <h6 className="footer-item__title">Quick Links</h6>
                        <ul className="footer-menu">
                            <li className="mb-12"><Link to="/shop" className="text-gray-600 hover-text-main-600 text-sm">All Products</Link></li>
                            <li className="mb-12"><Link to="/about" className="text-gray-600 hover-text-main-600 text-sm">About Us</Link></li>
                            <li className="mb-12"><Link to="/blog" className="text-gray-600 hover-text-main-600 text-sm">Blogs &amp; Stories</Link></li>
                            <li className="mb-12"><Link to="/contact" className="text-gray-600 hover-text-main-600 text-sm">Contact Us</Link></li>
                        </ul>
                    </div>

                    {/* Column 3: My Account */}
                    <div className="footer-item footer-col-account pe-xl-2 wow fadeInUp" data-wow-delay="0.3s">
                        <h6 className="footer-item__title">My Account</h6>
                        <ul className="footer-menu">
                            <li className="mb-12"><Link to="/account?tab=profile" className="text-gray-600 hover-text-main-600 text-sm">My Profile</Link></li>
                            <li className="mb-12"><Link to="/account?tab=orders" className="text-gray-600 hover-text-main-600 text-sm">Order History</Link></li>
                            <li className="mb-12"><Link to="/cart" className="text-gray-600 hover-text-main-600 text-sm">Shopping Cart</Link></li>
                            <li className="mb-12"><Link to="/wishlist" className="text-gray-600 hover-text-main-600 text-sm">Wishlist</Link></li>
                        </ul>
                    </div>

                    {/* Column 4: Policies */}
                    <div className="footer-item footer-col-policies pe-xl-2 wow fadeInUp" data-wow-delay="0.4s">
                        <h6 className="footer-item__title">Policies</h6>
                        <ul className="footer-menu">
                            <li className="mb-12"><Link to="/privacy-policy" className="text-gray-600 hover-text-main-600 text-sm">Privacy Policy</Link></li>
                            <li className="mb-12"><Link to="/terms-and-conditions" className="text-gray-600 hover-text-main-600 text-sm">Terms &amp; Conditions</Link></li>
                            <li className="mb-12"><Link to="/shipping-policy" className="text-gray-600 hover-text-main-600 text-sm">Shipping &amp; Delivery</Link></li>
                            <li className="mb-12"><Link to="/refund-policy" className="text-gray-600 hover-text-main-600 text-sm">Return &amp; Refund</Link></li>
                        </ul>
                    </div>

                    {/* Column 5: Contact Us */}
                    <div className="footer-item footer-col-contact wow fadeInUp" data-wow-delay="0.5s">
                        <h6 className="footer-item__title">Contact Us</h6>
                        <div className="footer-contact-item">
                            <span className="contact-icon flex-center rounded-circle bg-main-600 text-white text-md flex-shrink-0">
                                <i className="ph-fill ph-map-pin" />
                            </span>
                            <span className="contact-text text-gray-900">{address}</span>
                        </div>
                        <div className="footer-contact-item">
                            <span className="contact-icon flex-center rounded-circle bg-main-600 text-white text-md flex-shrink-0">
                                <i className="ph-fill ph-phone-call" />
                            </span>
                            <a href={`tel:${phone.replace(/\s+/g, '')}`} className="contact-text text-gray-900 hover-text-main-600">
                                {phone}
                            </a>
                        </div>
                        <div className="footer-contact-item">
                            <span className="contact-icon flex-center rounded-circle bg-main-600 text-white text-md flex-shrink-0">
                                <i className="ph-fill ph-envelope" />
                            </span>
                            <a href={`mailto:${email}`} className="contact-text text-gray-900 hover-text-main-600">
                                {email}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default FooterOne;
